import { useState, type ReactNode } from 'react'
import './Disclosure.css'

/**
 * A section that is closed until asked for.
 *
 * The lead drawer had ten bordered cards competing for attention, which meant
 * none of them had it. Everything that is reference rather than action lives
 * behind one of these instead: the heading and a count stay visible, so a rep
 * can see *that* there are six contacts without having to scroll past them.
 *
 * Deliberately not a `<details>` element — the summary needs a count on the
 * right and a chevron that animates, and styling the native marker across
 * browsers costs more than the twelve lines this takes.
 */
export function Disclosure({
  title,
  count,
  hint,
  defaultOpen = false,
  children,
}: {
  title: string
  /** Shown next to the title. A number a rep can act on without opening. */
  count?: number | string
  /** One short line under the title, visible while closed. */
  hint?: ReactNode
  defaultOpen?: boolean
  children: ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <section className={`disc${open ? ' disc--open' : ''}`}>
      <button className="disc__summary" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className="disc__chevron" aria-hidden="true">
          ›
        </span>
        <span className="disc__title">{title}</span>
        {count !== undefined && count !== '' && <span className="disc__count">{count}</span>}
        {hint && !open && <span className="disc__hint">{hint}</span>}
      </button>
      {open && <div className="disc__body">{children}</div>}
    </section>
  )
}
